import * as React from 'react'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'
import { getAppConfig } from '@feedback/utils/CONST'
import { getCurrentVestSite } from '@feedback/utils/siteConfig'
import { getTextByKey, reportFn, tracking } from '@feedback/utils/util'
import './index.scss'

const searchHomeIcon: string = require('@feedback/assets/vest/icons/search-home.svg')
const historyIcon: string = require('@feedback/assets/vest/icons/history.svg')
const contactIcon: string = require('@feedback/assets/vest/icons/contact.svg')
const backIcon: string = require('@feedback/assets/vest/icons/back.svg')

export interface VestLayoutProps {
  children: React.ReactNode
  /** 是否展示底部 history + Contact Us（首页 true；列表/详情等后续屏可关） */
  showBottomBar?: boolean
  /** 是否展示右上角搜索 icon（默认 true；nav 模式下忽略） */
  showSearch?: boolean
  /**
   * 若提供，顶部改为返回 + 标题（对照原型 webview-nav），
   * 隐藏 brand bar。列表/详情等二级屏使用。
   */
  title?: string
  /** nav 模式下是否展示返回（默认 true） */
  showBack?: boolean
  /** 自定义返回；默认 history.goBack，无历史则回首页 */
  onBack?: () => void
  /** 额外 class */
  className?: string
  /** 滚动内容区额外 class */
  bodyClassName?: string
  /**
   * 自定义底栏插槽（如 FAQ 详情 Contact Us CTA）。
   * 与 showBottomBar 互斥：有 bottomSlot 时优先渲染插槽。
   */
  bottomSlot?: React.ReactNode
}

/**
 * 马甲站共用壳：顶部 brand bar（或 nav）+ 可选底部固定栏。
 * 后续 faq-list / reskin 屏复用；首页单独在 body 内铺类别网格。
 */
const VestLayout: React.FC<VestLayoutProps> = ({
  children,
  showBottomBar = true,
  showSearch = true,
  title,
  showBack = true,
  onBack,
  className,
  bodyClassName,
  bottomSlot
}) => {
  const history = useHistory()
  const site = getCurrentVestSite()
  const { appid, logo } = getAppConfig() || {}
  const brandLogo = logo || site?.logo || ''
  const displayName = site?.displayName || site?.brandName || 'Feedback'
  const brandName = site?.brandName
  const isNavMode = typeof title === 'string'

  const goHome = () => {
    if (history.location.pathname !== '/') {
      history.push('/')
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    // 无浏览历史时回首页，避免空白
    if (history.length > 1) {
      history.goBack()
    } else {
      history.push('/')
    }
  }

  const onSearch = () => {
    // 复用现有搜索结果页入口；空 keyword 进入后可继续输入
    history.push('/searchResult')
  }

  const onHistory = () => {
    reportFn({ parm1: 1, parm2: 2, parm6: '' })
    history.push('/ticketList')
  }

  const onContact = () => {
    if (appid === 'cm1006') {
      tracking({
        _step_id: '',
        _feedback_page_id: 1,
        _postion_id: 3,
        _search_words: '',
        _faq_id: '',
        _src_faq_id: ''
      })
    } else {
      reportFn({ parm1: 1, parm2: 3, parm6: '' })
    }
    history.push('/ticketForm')
  }

  const hasBottom = Boolean(bottomSlot) || showBottomBar

  return (
    <div
      className={clsx(
        'vest-shell',
        hasBottom && 'has-bottom',
        isNavMode && 'is-nav',
        site?.siteKey,
        className
      )}
    >
      {isNavMode ? (
        <header className='vest-shell__nav'>
          {showBack ? (
            <button
              type='button'
              className='vest-shell__icon-btn vest-shell__back'
              aria-label='Back'
              onClick={handleBack}
            >
              <img src={backIcon} alt='' />
            </button>
          ) : (
            <span className='vest-shell__icon-btn' aria-hidden='true' />
          )}
          <h1 className='vest-shell__nav-title'>{title}</h1>
          {/* 右侧占位，保证标题视觉居中 */}
          <span className='vest-shell__icon-btn' aria-hidden='true' />
        </header>
      ) : (
        <header className='vest-shell__brand'>
          <button
            type='button'
            className='vest-shell__lockup'
            aria-label='Go to feedback home'
            onClick={goHome}
          >
            {brandName ? (
              <>
                {brandLogo ? (
                  <img
                    className='vest-shell__mark'
                    src={brandLogo}
                    alt=''
                    aria-hidden='true'
                  />
                ) : null}
                <span className='vest-shell__brand-name'>{brandName}</span>
              </>
            ) : brandLogo ? (
              <img
                className='vest-shell__logo'
                src={brandLogo}
                alt={displayName}
              />
            ) : (
              <span className='vest-shell__brand-name'>{displayName}</span>
            )}
          </button>
          {showSearch ? (
            <button
              type='button'
              className='vest-shell__icon-btn'
              aria-label='Search'
              onClick={onSearch}
            >
              <img src={searchHomeIcon} alt='' />
            </button>
          ) : (
            <span className='vest-shell__icon-btn' aria-hidden='true' />
          )}
        </header>
      )}

      <div
        className={clsx(
          'vest-shell__body',
          hasBottom && 'is-with-bottom',
          bodyClassName
        )}
      >
        <div className='vest-shell__scroll'>{children}</div>
      </div>

      {bottomSlot ? (
        <div className='vest-shell__slot-bottom'>{bottomSlot}</div>
      ) : showBottomBar ? (
        <footer className='vest-shell__bottom'>
          <button
            type='button'
            className='vest-shell__history'
            aria-label='History'
            onClick={onHistory}
          >
            <img src={historyIcon} alt='' />
          </button>
          <button
            type='button'
            className='vest-shell__contact'
            onClick={onContact}
          >
            <img src={contactIcon} alt='' />
            <span>{getTextByKey('Contact Us')}</span>
          </button>
        </footer>
      ) : null}
    </div>
  )
}

export default VestLayout
